import { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Button, Form, Input } from 'reactstrap';
import moment from 'moment';
import { PhoneInput } from 'react-international-phone';
import { errorMsg, successMsg } from '../../cache/vars';
import { DECLINE_SERVICE, GET_AVAILABLE_FILTERS_FOR_PACKAGE_INFO, GET_FILE_DOWNLOAD_LINK, SAVE_STAGE_ONE_CARD } from '../../queries/vip-workflow';
import { SERVICE_STATE } from '../../utils/constants';
import StagedCardLayout from '../StagedCard/StagedCardLayout/StagedCardLayout';
import CardDetails from '../StagedCard/CardDetails/CardDetails';
import CardGoogleMap from '../StagedCard/CardGoogleMap/CardGoogleMap';
import CardGuaranteed from '../StagedCard/CardGuaranteed/CardGuaranteed';
import CardAction from '../StagedCard/CardAction/CardAction';
import CardDeclineBtn from '../StagedCard/CardDeclineBtn/CardDeclineBtn';
import { generateMailtoLink, getSplittedName, truncateString, formatPhoneNumber } from '../../utils/services';
import bellIcon from '../../assets/img/icons/cards/bell-icon.svg';
import reservationIcon from '../../assets/img/icons/cards/reservation-icon.svg';
import ticketIcon from "../../assets/img/icons/common/ticket-icon.svg";
import bedIcon1 from '../../assets/img/icons/cards/bed-icon-1.svg';
import bedIcon2 from '../../assets/img/icons/cards/bed-icon-2.svg';
import flagIcon from '../../assets/img/icons/cards/flag-icon.svg';
import mapIcon from '../../assets/img/icons/cards/map-icon.svg';
import hotelIcon from "../../assets/img/icons/cards/hotel-icon.svg";
import helpIcon from '../../assets/img/icons/cards/help-icon.svg';
import partyIcon from '../../assets/img/icons/cards/party-icon.svg';
import GetHelpModal from '../Modal/GetHelpModal';
import { TicketActions } from '../Modal/TicketActions';
import { TicketsPreviewModal } from '../Modal/TicketsPreviewModal';

import './hotelGroupCardNew.scss';

type HotelGroupCardNewProps = {
  cart: any;
  datetime: any | null;
  time: any;
  isMissingInfo: boolean;
  isPublished: boolean;
  isServiceDeclined: boolean;
  serviceDeclined: string;
  packageDetailsId: number;
  setPackageOrderWithDetails: (data: any) => void;
  decryptedInvoiceId: any;
  setMissingInfoCards: any;
  cardDescription: string;
  cardGroup: string;
  cardsWithTickets: Record<string, any>;
  ticketsForCard: Record<string, any>;
};

const HotelGroupCardNew = ({
  cart,
  datetime,
  time,
  isMissingInfo,
  isPublished,
  isServiceDeclined,
  serviceDeclined,
  packageDetailsId,
  setPackageOrderWithDetails,
  decryptedInvoiceId,
  setMissingInfoCards,
  cardDescription,
  cardGroup,
  cardsWithTickets,
  ticketsForCard,
}: HotelGroupCardNewProps) => {
  const client = useApolloClient();
  
  const [file, setFile] = useState<string | undefined>("");
  const [ticket, setTicket] = useState<any | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | undefined>("");
  const [emailLink, setEmailLink] = useState("");

  const [cardState, setCardState] = useState<{ packageDetailsId: number; cartData: any }>({
    packageDetailsId,
    cartData: {},
  });
  const [isSubmitBtnDisabled, setIsSubmitBtnDisabled] = useState(true);

  const [isFieldsValid, setIsFieldsValid] = useState({
    checkInFirstName: true,
    checkInLastName: true,
    contactNumber: true,
    bedsNumber: true,
  });
  
  const handleDeclineClick = async () => {
    if (window.confirm('Are you sure you want to decline this service?')) {
      const { data } = await client.mutate({
        mutation: DECLINE_SERVICE,
        variables: { packageDetailsId: packageDetailsId },
      });
      if (data?.declineService) {
        setPackageOrderWithDetails((prev: { orderDetails: { packageDetailsId: number; cartDetails: any; }[]; }) => ({
          ...prev,
          orderDetails: prev.orderDetails.map((item: { packageDetailsId: number; cartDetails: any; }) =>
            item.packageDetailsId === packageDetailsId
              ? {
                ...item,
                cartDetails: {
                  ...item.cartDetails,
                  cartId: data?.declineService,
                  isServiceDeclined: SERVICE_STATE.DECLINED,
                },
              }
              : item
          ),
        }));
        successMsg('Service Declined');
      }
    }
  }

  useEffect(() => {
    const requiredFields = ['checkInFirstName', 'checkInLastName', 'contactNumber', 'bedsNumber'];

    const isRequiredFieldsEmpty = requiredFields.some((el) => {
      return !cardState.cartData[el];
    }) || cardState.cartData['contactNumber'] === '+' || cardState.cartData['contactNumber'].length <= 6;

    setIsSubmitBtnDisabled(isRequiredFieldsEmpty);
  }, [cardState.cartData]);
  
  useEffect(() => {
    generateMailtoLink(client, window.location.pathname, setEmailLink);
  }, [client]);

  const cartJsonData = JSON.parse(cart.cartDetails.cartJsonData);
  
  const tickets = ticketsForCard?.filter((ticket: any) => ticket.deliveryStatus === "UPLOADED" || ticket.eTicketLink);
  
  const reservationName = cartJsonData?.reservationName;
  const checkInFirstName = cartJsonData?.checkInFirstName;
  const checkInLastName = cartJsonData?.checkInLastName;
  
  const renderNameField = (label: {} | null | undefined, value: {} | null | undefined) => (
    <div className="hotel-card-content">
      <div className="card-content-column">
        <p style={{ fontSize: '14px', fontWeight: '500', fontFamily: "'Inter', sans-serif" }}>{label}</p>
      </div>
      <div className="card-content-column">
        <p style={{ fontSize: '14px', fontWeight: '400', fontFamily: "'Inter', sans-serif" }}>{value}</p>
      </div>
    </div>
  );
  

  return (
    <StagedCardLayout
      cardIcon={hotelIcon}
      cardTitle={cart?.cartDetails?.cardTitle || "Hotel Details"}
      datetime={datetime}
      time={time}
      isPublished={isPublished}
      isServiceDeclined={isServiceDeclined}
      serviceDeclined={serviceDeclined}
      isMissingInfo={isMissingInfo}
      cardGroup={cardGroup}
    >
      <>
        <GetHelpModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} tag={cardGroup.replaceAll(' ', '')} />
        {isPublished &&
          <>
          {cartJsonData?.date &&
            <>
              <CardDetails
                isToggleExpanded={false}
              >
                <div className="hotel-check-inout">
                  <img loading="lazy" src={bellIcon} alt="bell-icon" className="hotel-check-icon" />
                  <div className="hotel-check-details">
                    <div className="hotel-check-title">Check-in</div>
                    <div className="hotel-check-date">
                      {cartJsonData?.date ? moment(cartJsonData.date).format("ddd, MMM Do") : '-'}
                    </div>
                    <div className="hotel-check-time">
                      {cartJsonData?.date ? moment(cartJsonData.date).format("h:mm A") : '-'}
                    </div>
                  </div>
                  <div className="hotel-check-details">
                    <div className="hotel-check-title">Check-out</div>
                    <div className="hotel-check-date">
                      {cartJsonData?.date ? moment(cartJsonData.date).add(cartJsonData?.numberOfNights || 0, 'days').format("ddd, MMM Do") : '-'}
                    </div>
                  </div>
                </div>
              </CardDetails>
              
              <div className="divider"></div>
            </>
          }         
            
            {cartJsonData?.placeName &&
              <>
                <CardDetails
                  cardIcon={mapIcon}
                  cardTitle={cartJsonData?.hotelName || ''}
                  cardSubtitleGoogleLink={{
                    name: cartJsonData?.hotelName === cartJsonData?.hotelPlaceName ? truncateString(cartJsonData?.hotelName, 30) : cartJsonData?.hotelName,
                    address: !cartJsonData?.placeName ? truncateString(cartJsonData?.placeName, 30) : truncateString(cartJsonData?.placeName, 30),
                    link: cartJsonData?.placeLink, 
                    time: cartJsonData?.date || ''
                  }}
                >
                  {cartJsonData?.hotelPhone &&
                    <div className="hotel-card-content">
                      <p className="pr-2" style={{ fontSize: '14px', fontWeight: '500', fontFamily: "'Inter', sans-serif", color: "#101010" }}>Phone:</p>
                      <p style={{ fontSize: '14px', fontWeight: '400', fontFamily: "'Inter', sans-serif", color: "#101010" }}>
                        <a href={`tel:${cartJsonData?.hotelPhone}`}>{formatPhoneNumber(cartJsonData?.hotelPhone)}</a>
                      </p>
                    </div>
                  }
                  <CardGoogleMap address={cartJsonData?.placeName} className="pl-4" />
                </CardDetails>
              
                <div className="divider"></div>
              </>
            }
            
            <CardDetails
              cardIcon={reservationIcon}
              cardTitle="Reservation Details"
              cardSubtitle={`Nights ${cartJsonData.numberOfNights || 'TBD'}, ${cartJsonData.roomsNumber || 'TBD'} Room`}
            >
              {/* <div className="hotel-card-content">
                <div className="card-content-column">
                  {cartJsonData?.date && <p>Check-in</p>}
                  {cartJsonData?.reservationName && <p>Reservation First Name</p>}
                  {cartJsonData?.reservationName && <p>Reservation Last Name</p>}
                  {cartJsonData?.occupantsNumber && <p>Occupants</p>}
                  {cartJsonData?.confirmation && <p>Confirmation</p>}
                </div>
                <div className="card-content-column">
                  cartJsonData?.date && <p>{cartJsonData?.date ? moment(cartJsonData.date).format("h:mm a") : '-'}</p>}
                  {cartJsonData?.reservationName && <p>{cartJsonData?.reservationName.split(' ')[0]}</p>}
                  {cartJsonData?.reservationName && <p>{cartJsonData?.reservationName.split(' ')[1]}</p>}
                  {cartJsonData?.occupantsNumber && <p>{cartJsonData?.occupantsNumber}</p>}
                  {cartJsonData?.confirmation && <p>{cartJsonData?.confirmation}</p>}
                </div>
              </div> */}
              <div className="hotel-card-content">
                <div className="card-content-column">
                  {cartJsonData?.date && <p style={{ fontSize: '14px', fontWeight: '500', fontFamily: "'Inter', sans-serif" }}>Check-in</p>}
                </div>
                <div className="card-content-column">
                  {cartJsonData?.date && <p style={{ fontSize: '14px', fontWeight: '400', fontFamily: "'Inter', sans-serif" }}>{cartJsonData?.date ? moment(cartJsonData.date).format("h:mm a") : '-'}</p>}
                </div>
              </div>
              
              <>
                {reservationName && (!checkInFirstName && !checkInLastName) && (
                  <>
                    {renderNameField('Reservation First Name', getSplittedName(reservationName).firstName)}
                    {renderNameField('Reservation Last Name', getSplittedName(reservationName).lastName)}
                  </>
                )}
                {(checkInFirstName || checkInLastName) && (
                  <>
                    {checkInFirstName && renderNameField('Reservation First Name', checkInFirstName)}
                    {checkInLastName && renderNameField('Reservation Last Name', checkInLastName)}
                  </>
                )}
              </>

              <div className="hotel-card-content">
                <div className="card-content-column">
                  {cartJsonData?.occupantsNumber && <p style={{ fontSize: '14px', fontWeight: '500', fontFamily: "'Inter', sans-serif" }}>Occupants</p>}
                </div>
                <div className="card-content-column">
                  {cartJsonData?.occupantsNumber && <p style={{ fontSize: '14px', fontWeight: '400', fontFamily: "'Inter', sans-serif" }}>{cartJsonData?.occupantsNumber}</p>}
                </div>
              </div>
              <div className="hotel-card-content">
                <div className="card-content-column">
                  {cartJsonData?.confirmation && <p style={{ fontSize: '14px', fontWeight: '500', fontFamily: "'Inter', sans-serif" }}>Confirmation</p>}
                </div>
                <div className="card-content-column">
                  {cartJsonData?.confirmation && <p style={{ fontSize: '14px', fontWeight: '400', fontFamily: "'Inter', sans-serif" }}>{cartJsonData?.confirmation}</p>}
                </div>
              </div>
            </CardDetails>
            
            {!!tickets.length &&
              <>
                <div className="divider"></div>
                <CardDetails
                  cardIcon={ticketIcon}
                  cardTitle="Access Tickets now"
                  children={
                    <div className="mt-4">
                      {ticketsForCard && ticketsForCard.length ? (
                        ticketsForCard.map((ticket: any, index: number) => (
                          <TicketActions
                            ticket={ticket}
                            setFile={setFile}
                            setIsModalOpened={setIsModalOpened}
                            setTicket={setTicket}
                          />
                        ))
                      ) : (
                        <p>No tickets available</p>
                      )}
                    </div>
                  }
                />
              </>
            }
            
            {cartJsonData?.additionalRequests &&
              <>
                <div className="divider"></div>
                
                <CardDetails
                  cardIcon={flagIcon}
                  cardTitle="Additional Requests"
                  cardSubtitle={cartJsonData?.additionalRequests}
                />
              </>
            }
            
            {cartJsonData?.celebration &&
              <>
                <div className="divider"></div>
                
                <CardDetails
                  cardIcon={partyIcon}
                  cardTitle="Celebration"
                  cardSubtitle={cartJsonData?.celebration}
                />
              </>
            }
            
            <div className="divider"></div>
            
            {cartJsonData?.notes &&
              <>
                <CardDetails
                  cardIcon={flagIcon}
                  cardTitle="Additional Notes"
                  cardDescription={cartJsonData?.notes}
                />
                
                <div className="divider"></div>
              </>
            }
            
            <CardAction icon={helpIcon} text="Get help" 
              handleClick={() => {
                setIsModalOpen(true)
              }}
            />
          </>
        }

        {!isPublished && isMissingInfo && (
          <>
            <CardDetails
              isEditable={true}
              cardIcon={reservationIcon}
              cardTitle="Reservation Details"
              cardSubtitle="Please provide your contact info"
            >
              <>
                <div className="divider"></div>
                <Form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const requiredFields = ['checkInFirstName', 'checkInLastName', 'contactNumber', 'bedsNumber'];

                    const isRequiredFieldsEmpty = requiredFields.some((el) => !cardState.cartData[el]);

                    if (isRequiredFieldsEmpty) {
                      setIsFieldsValid({
                        checkInFirstName: !!cardState.cartData?.checkInFirstName,
                        checkInLastName: !!cardState.cartData?.checkInLastName,
                        contactNumber: !!cardState.cartData?.contactNumber,
                        bedsNumber: !!cardState.cartData?.bedsNumber,
                      });

                      errorMsg('Required Fields Should Not Be Empty');
                      return;
                    }

                    const { data } = await client.mutate({
                      mutation: SAVE_STAGE_ONE_CARD,
                      variables: { editCartDetailsInput: cardState },
                    });

                    const filtersData = await client.query({
                      query: GET_AVAILABLE_FILTERS_FOR_PACKAGE_INFO,
                      variables: { invoiceId: decryptedInvoiceId },
                      fetchPolicy: 'no-cache',
                    });

                    if (data) {
                      setPackageOrderWithDetails((prev: any) => ({
                        ...prev,
                        orderDetails: prev.orderDetails.map((cart: any) =>
                          cart.packageDetailsId === packageDetailsId
                            ? {
                              ...cart,
                              cartDetails: {
                                ...cart.cartDetails,
                                cardTitle: data?.saveStageOneCard.cardTitle,
                                cartId: data?.saveStageOneCard.cartId,
                                cartJsonData: data?.saveStageOneCard.cartJsonData,
                                isPublished: data?.saveStageOneCard.isPublished,
                                packageDetailsId: data?.saveStageOneCard.packageDetailsId,
                              },
                            }
                            : cart
                        ),
                      }));

                      setMissingInfoCards(filtersData.data?.getAvailableFiltersForPackageInfo.showMissingInfoFor);
                      successMsg('Saved');
                    }
                  }}
                >
                  <div className="inputs-container">
                    <div>
                      <p className="section-title" style={{ fontSize: '20px', fontWeight: '400', fontFamily: "'Inter', sans-serif" }}>Contact Details</p>
                      <p className="required-note" style={{ fontSize: '14px', fontWeight: '400', fontFamily: "'Inter', sans-serif" }}>* These fields are required.</p>
                    </div>
                    <div className="input-container-names">
                      <div className="input-container mr-2">
                        <label className={!isFieldsValid.checkInFirstName ? 'label-invalid' : ''}>Check-in First Name</label>
                        <div className="input-box">
                          <Input
                            className={!isFieldsValid.checkInFirstName ? 'input-invalid' : ''}
                            type="text"
                            placeholder="Check-in First Name*"
                            name="checkInFirstName"
                            invalid={!isFieldsValid.checkInFirstName}
                            onChange={(e) =>
                              setCardState((prev) => ({
                                ...prev,
                                cartData: { ...prev.cartData, [e.target.name]: e.target.value },
                              }))
                            }
                          />
                          <span></span>
                        </div>
                      </div>
                      <div className="input-container">
                        <label className={!isFieldsValid.checkInLastName ? 'label-invalid' : ''}>Check-in Last Name</label>
                        <div className="input-box">
                          <Input
                            className={!isFieldsValid.checkInLastName ? 'input-invalid' : ''}
                            type="text"
                            placeholder="Check-in Last Name*"
                            name="checkInLastName"
                            invalid={!isFieldsValid.checkInLastName}
                            onChange={(e) =>
                              setCardState((prev) => ({
                                ...prev,
                                cartData: { ...prev.cartData, [e.target.name]: e.target.value },
                              }))
                            }
                          />
                          <span></span>
                        </div>
                      </div>
                    </div>
                    <div className="input-container">
                      <label className={!isFieldsValid.contactNumber ? 'label-invalid' : ''}>Contact Number</label>
                      <div className="input-box">
                        <PhoneInput
                          className={!isFieldsValid.contactNumber ? "input-invalid" : ""}
                          style={{ marginTop: "8px" }}    
                          dialCodePreviewStyleProps={{style: { zIndex: 2, backgroundColor: 'red' }}}
                          countrySelectorStyleProps={{ buttonStyle: { height: "46px", marginTop: 0 }}}
                          inputStyle={{ borderLeft: 0, height: "46px", marginTop: 0 }}
                          defaultCountry="us"
                          value={cardState?.cartData?.contactNumber || ''}
                          onChange={(value: any) => {
                            if (setCardState) {
                              setCardState((prev) => ({
                                ...prev,
                                cartData: {
                                  ...prev.cartData,
                                  contactNumber: value,
                                },
                              }));
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="divider"></div>

                  <div className="inputs-container bed-request">
                    <div>
                      <label className="bed-request-label">Bed Request</label>
                      <p className="required-note" style={{ fontSize: '14px', fontWeight: '400', fontFamily: "'Inter', sans-serif" }}>* These fields are required.</p>
                      <p className="bed-request-note">Bed requests are not guaranteed but will be requested on your behalf.</p>
                    </div>
                    <div className="bed-options">
                      <button
                        type="button"
                        className={`bed-option ${cardState.cartData.bedsNumber === '1' ? 'active' : ''}`}
                        onClick={() =>
                          setCardState((prev) => ({
                            ...prev,
                            cartData: { ...prev.cartData, bedsNumber: '1' },
                          }))
                        }
                      >
                        <img src={bedIcon1} alt="1 Bed Icon" />
                        1 Bed
                      </button>
                      <button
                        type="button"
                        className={`bed-option ${cardState.cartData.bedsNumber === '2' ? 'active' : ''}`}
                        onClick={() =>
                          setCardState((prev) => ({
                            ...prev,
                            cartData: { ...prev.cartData, bedsNumber: '2' },
                          }))
                        }
                      >
                        <img src={bedIcon2} alt="2 Beds Icon" />
                        2 Beds
                      </button>
                    </div>
                    {!isFieldsValid.bedsNumber && <p className="required-note">Choose one bed option.</p>}
                  </div>
                  
                  <div className="divider"></div>
                    
                    <div>
                      <p style={{ fontSize: '20px', fontWeight: '400', fontFamily: "'Inter', sans-serif" }}>Note</p>
                      <p style={{ fontSize: '12px', fontWeight: '400', fontFamily: "'Inter', sans-serif" }} className="optional-note">We’ll arrange transportation to your hotel - no address needed. If you'd prefer a different drop-off location, enter it here.</p>
                    </div>
                  
                  <div className="divider"></div>
                
                  <div className="inputs-container">
                    <p className="section-title">
                      Additional Requests
                    </p>
                    <div className="input-containers">
                      <div className="input-container">
                        <div className="input-box">
                          <Input
                            type="textarea"
                            placeholder="Add special requests here..."
                            name="additionalRequests"
                            onChange={(e) =>
                              setCardState((prev) => ({
                                ...prev,
                                cartData: { ...prev.cartData, [e.target.name]: e.target.value },
                              }))
                            }
                          />
                          <span></span>
                        </div>
                      </div>
                      <div className="input-container">
                        <div className="input-box">
                          <Input
                            type="textarea"
                            placeholder="Are you celebrating a special occasion?"
                            name="celebration"
                            onChange={(e) =>
                              setCardState((prev) => ({
                                ...prev,
                                cartData: { ...prev.cartData, [e.target.name]: e.target.value },
                              }))
                            }
                          />
                          <span></span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="actions-container">
                    {!isServiceDeclined && <CardDeclineBtn handleClick={handleDeclineClick} />}
                    <Button disabled={isSubmitBtnDisabled} className="btn-round btn-icon d-flex align-items-center save-btn" type="submit">
                      Save
                    </Button>
                  </div>
                </Form>
              </>
            </CardDetails>
            <div className="divider"></div>
            <CardAction icon={helpIcon} text="Get help" 
              handleClick={() => {
                setIsModalOpen(true)
              }}
            />
          </>
        )}
        
        {!isPublished && !isMissingInfo &&
          <>
            <CardDetails
              isToggleExpanded={false}
              cardIcon={reservationIcon}
              cardTitle="Hotel Accomodations"
            >
              <div className="pl-4 mb-4">
                <CardGuaranteed text={isServiceDeclined && serviceDeclined !== 'UNDECLINED' ? "You’ve declined this service." : "Details will be provided as the event approaches."} subtext={isServiceDeclined ? "If you need further assistance please use the Get Help button." : ""} />
                {!isServiceDeclined &&
                  <>
                    <div className="divider"></div>

                    <div className="actions-container">
                      <CardDeclineBtn handleClick={handleDeclineClick} />
                    </div>
                  </>
                }
                
                <div className="divider"></div> 
                <CardAction icon={helpIcon} text="Get help" 
                  handleClick={() => {
                    setIsModalOpen(true)
                  }}
                />
              </div>
            </CardDetails>
          
            {cartJsonData?.notes &&
              <CardDetails
                cardIcon={flagIcon}
                cardTitle="Additional Notes"
                cardDescription={cartJsonData.notes}
              />
            }
          </>
        }
        
        <TicketsPreviewModal
          isModalOpened={isModalOpened}
          setIsModalOpened={setIsModalOpened}
          file={file}
          ticket={ticket}
        />
      </>
    </StagedCardLayout>
  );
};

export default HotelGroupCardNew;
